import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ifDefined,
  paths,
  t,
  broadcastReceiver,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
    focusDelay: {
      type: Number,
      attribute: 'focus-delay',
    },
    focusElementOnClose: {
      type: String,
      attribute: 'focus-element-on-close',
    },
    modalLabelledBy: {
      type: String,
      attribute: 'modal-labelled-by',
    },
    ariaLabel: {
      type: String,
      attribute: 'label',
    },
  }

  constructor() {
    super()
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      // TODO: not sure if this is the best behaviour, but givem that all the videos are meant to be muted and just loop, I think I'd rather have them "running" in the background while a modal is open than worry about restarting them
      // utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.classList.remove('hidden')
    this.receiver = broadcastReceiver('modal', (...args) => {
      // when passing the data attribute from the <hh-broadcast> component
      // id needs to be first and action second; action is optional
      const { id = null, action = null } = args[0]
      if (!action || action === 'open') this.openFromDocument(...args)
      if (action === 'close') this.closeFromDocument(...args)
    })

    this.openFromDocument = this.openFromDocument.bind(this)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()
  }

  initCloseOnEscape(event) {
    if (event.code.toUpperCase() === 'ESCAPE' && this.open) {
      this.open = false
      document.documentElement.classList.remove('overflow-hidden')
      this.handleFocusElementOnClose()
    }
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true
      document.documentElement.classList.add('overflow-hidden')
      document.addEventListener('keydown', (event) =>
        this.initCloseOnEscape(event)
      )
      this.opener = data.element
    } else {
      this.open = false
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }
  }

  closeFromDocument(data) {
    if (this.id === data.id) {
      const event = { type: 'click' } // fake a js event obj
      this.close(event)
      document.removeEventListener('keydown', (event) =>
        this.initCloseOnEscape(event)
      )
    }
  }

  close(event) {
    if (event.type === 'click') {
      document.documentElement.classList.remove('overflow-hidden')

      this.open = false
      this.handleFocusElementOnClose()
      return
    }
  }

  handleFocusElementOnClose() {
    const focusCandidates = []

    if (this.focusElementOnClose) {
      const element = document.getElementById(this.focusElementOnClose)
      const focusable =
        element?.shadowRoot?.querySelector('a, button, input') || element
      if (focusable) focusCandidates.push(focusable)
    }

    if (this.opener != null) {
      focusCandidates.push(
        document.getElementById(`main-mobile-${this.opener}`),
        document.getElementById(`main-${this.opener}`)
      )
    }

    const focusTarget = focusCandidates.find(
      (element) => element != null && element.offsetParent !== null
    )

    if (focusTarget) {
      setTimeout(() => {
        focusTarget.focus({
          focusVisible: false,
        })
      }, this.focusDelay || 0)
    }
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      aria-labelledby="${ifDefined(this.modalLabelledBy)}"
      aria-label="${ifDefined(this.ariaLabel)}"
    >
      <hh-focus-trap active="${this.open}" delay="${this.focusDelay}">
        <div class="content" role="document">
          <button
            @click="${this.close}"
            class="button"
            type="button"
            aria-label="${t('accessibility.close')}"
            part="close-btn"
          >
            ${unsafeHTML(paths.close)}
          </button>
          <slot></slot>
        </div>
      </hh-focus-trap>
    </div>`
  }

  static styles = css`
    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .button {
      aspect-ratio: 1;
      width: 3rem;
      position: fixed;
      top: 4rem;
      right: 4rem;
      display: flex;
      background-color: var(--hh-background);
      border: 0.1rem solid var(--hh-border-color);
      border-radius: 50%;
      color: var(--hh-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 2;
    }

    .button svg {
      width: 8px;
    }

    :host([ctx='product']) .button {
      background: var(--hh-color-reverse);
    }

    :host([ctx='product']) .button svg {
      width: 16px;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: auto;
      width: 100%;
      padding: var(--hh-padding);
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('hh-modal', HHModal)
